import React, { useMemo, useEffect, useState } from "react";
import { useData } from "../context/DataContext";
import styles from "../Assets/css/fleet.module.css";
import PPAMap from "./PPAMap";
import logoSvg from "../Assets/images/logo.svg";
import { useNavigate } from "react-router-dom";
import Loader from "../component/Loader";
import Select from "react-select";
import { US_STATES } from "../context/DataContext";

const PPAProjects = () => {
  const {
    data: contextData,
    unifiedDevices: contextUnifiedDevices,
    utilities: contextUtilities,
    loading: contextLoading,
    fetchAllData,
  } = useData();

  const Navigate = useNavigate();
  const [selectedState, setSelectedState] = useState("All");
  const [selectedUtility, setSelectedUtility] = useState("All");
  const [selectedDaylight, setSelectedDaylight] = useState("Daylight");

  const loading = contextLoading;

  useEffect(() => {
    const storedAccessToken = localStorage.getItem("accessToken");
    if (storedAccessToken) {
      fetchAllData(storedAccessToken);
    }
  }, []);

  const unifiedDevicesList = contextUnifiedDevices || [];

  // Enphase = Inverter devices from texture API
  const enphaseDevices = useMemo(() => {
    if (!contextData) return [];
    return contextData.filter(d => {
      const type = d.device_type?.toLowerCase().replace(/\s+/g, "");
      return type === "inverter";
    });
  }, [contextData]);

  // Only PPA device types: Hoymiles, Sol-Ark, Enphase, Franklin
  const ppaDeviceCounts = useMemo(() => {
    const hoymilesCount = unifiedDevicesList.filter(d => d.type === "Hoymiles").length;
    const solarkCount = unifiedDevicesList.filter(d => d.type === "Sol-Ark").length;
    const enphaseCount = enphaseDevices.length;
    const franklinCount = unifiedDevicesList.filter(d => d.type === "Franklin").length;

    return {
      Hoymiles: hoymilesCount,
      "Sol-Ark": solarkCount,
      Enphase: enphaseCount,
      Franklin: franklinCount,
    };
  }, [unifiedDevicesList, enphaseDevices]);

  // Filtered counts based on Daylight filter
  const filteredDeviceCounts = useMemo(() => {
    if (selectedDaylight === "All") return ppaDeviceCounts;
    if (selectedDaylight === "Daylight") {
      return {
        Hoymiles: ppaDeviceCounts["Hoymiles"] || 0,
        "Sol-Ark": ppaDeviceCounts["Sol-Ark"] || 0,
        Enphase: ppaDeviceCounts["Enphase"] || 0,
      };
    }
    // AppUser
    return {
      Franklin: ppaDeviceCounts["Franklin"] || 0,
    };
  }, [ppaDeviceCounts, selectedDaylight]);

  // Capacity calculations based on filtered devices
  const totalCapacity = useMemo(() => {
    let capacity = 0;
    capacity += (filteredDeviceCounts["Sol-Ark"] || 0) * 5;
    capacity += (filteredDeviceCounts["Hoymiles"] || 0) * 3;
    capacity += (filteredDeviceCounts["Enphase"] || 0) * 5;
    capacity += (filteredDeviceCounts["Franklin"] || 0) * 5;
    return capacity;
  }, [filteredDeviceCounts]);

  const handleDetailsClick = (deviceType) => {
    const normalized = deviceType.toLowerCase().replace(/[\s-]+/g, "");

    switch (normalized) {
      case "solark":
        Navigate("/SolArkDetails");
        break;
      case "hoymiles":
        Navigate("/HoymilesDetails");
        break;
      case "enphase":
        Navigate("/InverterDetails");
        break;
      case "franklin":
        Navigate("/FranklinUnifiedDetails");
        break;
      default:
        console.error(`No navigation found for device type: ${deviceType}`);
        break;
    }
  };

  return (
    <div className={styles.fleet_view_cantaint}>
      {loading && <Loader />}

      {/* Topbar */}
      <section className={styles.topbar}>
        <div className={styles.container}>
          <img className={styles.topbar_logo} src={logoSvg} alt="Topbar Logo" />
        </div>
        <div className={styles.topbar_right}>
          <button
            style={{
              backgroundColor: '#fff',
              color: '#FF5900',
              border: 'none',
              borderRadius: '50px',
              padding: '8px 24px',
              fontSize: '16px',
              fontWeight: '600',
              fontFamily: "'Forma DJR Deck', sans-serif",
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              textDecoration: 'none',
            }}
            onClick={() => window.open("/Home", "_blank")}
            onMouseEnter={(e) => (e.target.style.backgroundColor = '#ffe0cc')}
            onMouseLeave={(e) => (e.target.style.backgroundColor = '#fff')}
          >
            Fleet View
          </button>
          <div
            onClick={() => {
              localStorage.removeItem("accessToken");
              localStorage.removeItem("refreshToken");
              Navigate("/");
            }}
          >
            Logout
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className={styles.fleet_view}>
        <div className={styles.container}>
          <div className="row">
            <div className="col-md-12">
              <div className={styles.view_header}>
                <h2 className={styles.main_title}>Daylight Fleet dashboard</h2>
                <div className={styles.view_header_right}>
                  <div style={{ width: "280px", zIndex: 100 }}>
                    <Select
                      options={[
                        { value: "All", label: "All Utility" },
                        ...(contextUtilities || []).map((utility) => ({
                          value: utility.id.toString(),
                          label: utility.utility_name,
                        })),
                      ]}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          backgroundColor: "#fff",
                          borderColor: "#b6b6b6",
                          minHeight: "35px",
                          height: "35px",
                          boxShadow: "none",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#4c2806",
                          borderRadius: "3px",
                          cursor: "pointer",
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          height: "35px",
                          padding: "0 6px",
                          display: "flex",
                          alignItems: "center",
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: "#4c2806",
                          position: "relative",
                          top: "auto",
                          transform: "none",
                          margin: 0,
                          maxWidth: "100%",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }),
                        input: (provided) => ({
                          ...provided,
                          margin: "0px",
                          padding: "0px",
                        }),
                        indicatorSeparator: () => ({ display: "none" }),
                        indicatorsContainer: (provided) => ({
                          ...provided,
                          height: "35px",
                        }),
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 9999,
                          fontSize: "13px",
                          color: "#4c2806",
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected
                            ? "#ffe0b2"
                            : state.isFocused
                              ? "#fff7e9"
                              : "white",
                          color: "#4c2806",
                          cursor: "pointer",
                        }),
                      }}
                      value={
                        selectedUtility === "All"
                          ? { value: "All", label: "All Utility" }
                          : (contextUtilities || [])
                            .map((u) => ({
                              value: u.id.toString(),
                              label: u.utility_name,
                            }))
                            .find((opt) => opt.value === selectedUtility)
                      }
                      onChange={(option) =>
                        setSelectedUtility(option ? option.value : "All")
                      }
                      placeholder="Select Utility"
                      isSearchable
                      isClearable
                    />
                  </div>
                  <div style={{ width: "200px", zIndex: 100 }}>
                    <Select
                      options={[
                        { value: "All", label: "All States" },
                        ...US_STATES.map((state) => ({
                          value: state,
                          label: state,
                        })),
                      ]}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          backgroundColor: "#fff",
                          borderColor: "#b6b6b6",
                          minHeight: "35px",
                          height: "35px",
                          boxShadow: "none",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#4c2806",
                          borderRadius: "3px",
                          cursor: "pointer",
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          height: "35px",
                          padding: "0 6px",
                          display: "flex",
                          alignItems: "center",
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: "#4c2806",
                          position: "relative",
                          top: "auto",
                          transform: "none",
                          margin: 0,
                          maxWidth: "100%",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }),
                        input: (provided) => ({
                          ...provided,
                          margin: "0px",
                          padding: "0px",
                        }),
                        indicatorSeparator: () => ({ display: "none" }),
                        indicatorsContainer: (provided) => ({
                          ...provided,
                          height: "35px",
                        }),
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 9999,
                          fontSize: "13px",
                          color: "#4c2806",
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected
                            ? "#ffe0b2"
                            : state.isFocused
                              ? "#fff7e9"
                              : "white",
                          color: "#4c2806",
                          cursor: "pointer",
                        }),
                      }}
                      value={
                        selectedState === "All"
                          ? { value: "All", label: "All States" }
                          : { value: selectedState, label: selectedState }
                      }
                      onChange={(option) =>
                        setSelectedState(option ? option.value : "All")
                      }
                      placeholder="Select State"
                      isSearchable
                      isClearable
                    />
                  </div>
                  <div style={{ width: "200px", zIndex: 100 }}>
                    <Select
                      options={[
                        { value: "All", label: "All" },
                        { value: "Daylight", label: "Daylight" },
                        { value: "AppUser", label: "AppUser" },
                      ]}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          backgroundColor: "#fff",
                          borderColor: "#b6b6b6",
                          minHeight: "35px",
                          height: "35px",
                          boxShadow: "none",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#4c2806",
                          borderRadius: "3px",
                          cursor: "pointer",
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          height: "35px",
                          padding: "0 6px",
                          display: "flex",
                          alignItems: "center",
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: "#4c2806",
                          position: "relative",
                          top: "auto",
                          transform: "none",
                          margin: 0,
                          maxWidth: "100%",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }),
                        input: (provided) => ({
                          ...provided,
                          margin: "0px",
                          padding: "0px",
                        }),
                        indicatorSeparator: () => ({ display: "none" }),
                        indicatorsContainer: (provided) => ({
                          ...provided,
                          height: "35px",
                        }),
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 9999,
                          fontSize: "13px",
                          color: "#4c2806",
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected
                            ? "#ffe0b2"
                            : state.isFocused
                              ? "#fff7e9"
                              : "white",
                          color: "#4c2806",
                          cursor: "pointer",
                        }),
                      }}
                      value={{ value: selectedDaylight, label: selectedDaylight }}
                      onChange={(option) =>
                        setSelectedDaylight(option ? option.value : "Daylight")
                      }
                      placeholder="Daylight Filter"
                      isSearchable={false}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Left Column: Map + Device List */}
            <div className="col-md-6">
              <div className={styles.fleet_view_left}>
                <div className={styles.location_filter}>
                  <PPAMap
                    unifiedDevices={contextUnifiedDevices}
                    enphaseDevices={enphaseDevices}
                    selectedState={selectedState}
                    selectedUtility={selectedUtility}
                    utilities={contextUtilities}
                    selectedDaylight={selectedDaylight}
                  />
                </div>
                <div className={styles.device_counts}>
                  <div className={styles.tableUpper}>
                    <h4>Device</h4>
                    <h4 className={styles.countss}>Counts</h4>
                    <h4>Details</h4>
                  </div>
                  <div className="table-responsive">
                    <table className={styles.table} style={{ width: "100%" }}>
                      <tbody>
                        {Object.entries(filteredDeviceCounts).map(
                          ([deviceType, count], index) => (
                            <tr key={index} className={styles.tableTR}>
                              <td className={styles.devicetype}>
                                {deviceType}
                              </td>
                              <td className={styles.Counttd}>{count}</td>
                              <td
                                className={styles.details}
                                onClick={() => handleDetailsClick(deviceType)}
                              >
                                Details
                              </td>
                            </tr>
                          )
                        )}
                        <tr className={styles.tableborder}></tr>
                        <tr className={styles.tableTR}>
                          <td style={{ width: "55%" }}>
                            <b>Total</b>
                          </td>
                          <td
                            style={{ width: "50%", marginLeft: "-44px"}}
                            className={styles.countin}
                          >
                            <b>
                              {Object.values(filteredDeviceCounts).reduce(
                                (total, count) => total + count,
                                0
                              )}
                            </b>
                          </td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Capacity Info */}
            <div className="col-md-6">
              <div className={styles.fleet_view_left}>
                <div className={styles.dispatch_available_capacity}>
                  <h3>Daylight Fleet Available Capacity:</h3>
                  <div className={`row ${styles.customRow}`}>
                    <div className="col-xl-6 col-lg-12 col-md-12 col-sm-6">
                      <div className={styles.card_box}>
                        <p>
                          <b>Total PPA Devices</b>
                          <br />
                          <span className={styles.kwh}>
                            {Object.values(filteredDeviceCounts).reduce(
                              (t, c) => t + c,
                              0
                            )}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="col-xl-6 col-lg-12 col-md-12 col-sm-6">
                      <div className={styles.card_box}>
                        <p>
                          <b>Generating Capacity</b>
                          <br />
                          <span className={styles.kwh}>
                            {totalCapacity} kWh
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="col-xl-6 col-lg-12 col-md-12 col-sm-6">
                      <div className={styles.card_box}>
                        <p>
                          <b>Hoymiles</b>
                          <br />
                          <span className={styles.kwh}>
                            {filteredDeviceCounts["Hoymiles"] || 0} devices
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="col-xl-6 col-lg-12 col-md-12 col-sm-6">
                      <div className={styles.card_box}>
                        <p>
                          <b>Sol-Ark</b>
                          <br />
                          <span className={styles.kwh}>
                            {filteredDeviceCounts["Sol-Ark"] || 0} devices
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="col-xl-6 col-lg-12 col-md-12 col-sm-6">
                      <div className={styles.card_box}>
                        <p>
                          <b>Enphase</b>
                          <br />
                          <span className={styles.kwh}>
                            {filteredDeviceCounts["Enphase"] || 0} devices
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="col-xl-6 col-lg-12 col-md-12 col-sm-6">
                      <div className={styles.card_box}>
                        <p>
                          <b>Franklin</b>
                          <br />
                          <span className={styles.kwh}>
                            {filteredDeviceCounts["Franklin"] || 0} devices
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PPAProjects;
